import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "search-container" }
const _hoisted_2 = { class: "include-child-object-container" }
const _hoisted_3 = { class: "table-container" }

import { ref, computed, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { useGeneralStore } from '@/pinia'
import ButtonOutline from '@/design-system/atoms/buttons/ButtonOutline.vue'
import TableLinksMini from '@/components/features/attachments/TableLinksMini.vue'
import OverlayCard from '@/design-system/molecules/OverlayCard.vue'
import InfoLabel from '@/design-system/molecules/InfoLabel.vue'
import InputSearch from '@/design-system/molecules/InputSearch.vue'
import ConfirmDeleteAttachmentCard from '@/components/features/attachments/ConfirmDeleteAttachmentCard.vue'
import CheckBoxStandard from '@/design-system/atoms/CheckBoxStandard.vue'
import {
  archiveAttachments,
  deleteAttachments,
  AttachmentsPage,
  Attachment,
  getAttachmentsPage
} from '@/services/attachments.service'
import { orderAlphabetical } from '@/utils/sort'
import { debounce } from '@/utils/debounce'

type Emits = {
  (e: 'toggleFullscreen'): void
  (e: 'closeDashboard'): void
}

export default /*@__PURE__*/_defineComponent({
  __name: 'LinksDashboard',
  emits: ["toggleFullscreen", "closeDashboard"],
  setup(__props) {


const { t } = useI18n()
const piniaStore = useGeneralStore()
const store = useStore()

const isFeatureFullscreen = computed(() => {
  return piniaStore.isFeatureFullscreen
})
const linksList = computed(() => {
  return piniaStore.linksList
})
const selectedObject = computed(() => {
  return store.getters.selectedObject
})

const clearInput = ref(false)
const includeChildObject = ref(false)
const linksPage = ref<AttachmentsPage<Attachment>>()
const pageNumber = ref(0)
const searchString = ref('')
const showConfirmDelete = ref(false)
const showLinkMenu = ref(false)

const fields = computed(() => {
  const fields = [
    {
      key: 'name',
      name: linksPage.value
        ? linksPage.value.totalElements + ' ' + t('general__data__links').toLocaleLowerCase()
        : ''
    }
  ]
  return fields
})
const linksUpdated = computed(() => {
  return piniaStore.linksUpdated
})
const mobileScreen = computed(() => {
  return store.state.mobileScreen
})
const isSearchActive = computed(() => {
  return searchString.value.length >= 1
})
async function archiveLink() {
  if (!piniaStore.selectedLink) return
  try {
    const id = piniaStore.selectedLink.attachmentId
    if (!id) return
    const response = await archiveAttachments([id])
    // For future bulk operations, check "succeeded" array for each link instead of current "success" boolean
    if (response?.data.success) {
      // remove link from linksList in store
      const linksArray = linksList.value.filter((link) => link.attachmentId !== id)
      piniaStore.setLinksList(linksArray)
      piniaStore.setSelectedLink(null)
      showLinkMenu.value = false
    }
    if (!response?.data.success) {
      store.commit('setInfoMessage', {
        type: 'error',
        message: `${t('general__data__link-archive-failed')}, ${t(
          'general__text__retry-later-again'
        ).toLowerCase()}`
      })
    }
  } catch (e) {
    store.commit('setInfoMessage', {
      type: 'error',
      message: `${t('general__data__link-archive-failed')}, ${t(
        'general__text__retry-later-again'
      ).toLowerCase()}`
    })
    console.error(e)
  }
}
async function deleteLink() {
  if (!piniaStore.selectedLink) return
  try {
    const id = piniaStore.selectedLink.attachmentId
    if (!id) return
    const response = await deleteAttachments([piniaStore.selectedLink.attachmentId])
    // For future bulk operations, check "succeeded" array for each link instead of current "success" boolean
    if (response?.data.success) {
      // remove link from linksList in store
      const linksArray = linksList.value.filter((link) => link.attachmentId !== id)
      piniaStore.setLinksList(linksArray)
      piniaStore.setSelectedLink(null)
      showLinkMenu.value = false
    }
    if (!response?.data.success) {
      store.commit('setInfoMessage', {
        type: 'error',
        message: `${t('general__data__link-delete-failed')}, ${t(
          'general__text__retry-later-again'
        ).toLowerCase()}`
      })
    }
  } catch (e) {
    store.commit('setInfoMessage', {
      type: 'error',
      message: `${t('general__data__link-delete-failed')}, ${t(
        'general__text__retry-later-again'
      ).toLowerCase()}`
    })
    console.error(e)
  }
}
async function getItems(entityId: string, page: number) {
  const linksArray: Attachment[] = []
  linksPage.value = await getAttachmentsPage(
    entityId,
    ['LINK'],
    page,
    25,
    'name,asc',
    null,
    includeChildObject.value
  )
  linksPage.value?.content.forEach((item) => {
    linksArray.push(item)
  })
  linksArray.sort(orderAlphabetical)
  piniaStore.setLinksList(linksArray)
}
async function getSearchItems(
  entityId: string,
  searchFilter: string,
  pageNumber: number,
  pageSize = 25
) {
  const linksArray: Attachment[] = []
  linksPage.value = await getAttachmentsPage(
    entityId,
    ['LINK'],
    pageNumber,
    pageSize,
    'name,asc',
    searchFilter,
    includeChildObject.value
  )
  linksPage.value?.content.forEach((item) => {
    linksArray.push(item)
  })
  linksArray.sort(orderAlphabetical)
  piniaStore.setLinksList(linksArray)
}
function loadMoreLinks() {
  pageNumber.value++
  if (pageNumber.value === linksPage.value?.totalPages) {
    return
  }
  if (isSearchActive.value) {
    getSearchItems(store.getters.selectedObjectId, searchString.value, pageNumber.value)
  } else {
    getItems(store.getters.selectedObjectId, pageNumber.value)
  }
}
function refreshList() {
  if (isSearchActive.value) {
    resetAndSearchLinks(searchString.value)
  } else {
    resetAndGetLinkList()
  }
}
const refreshListDebounce = debounce(() => {
  if (isSearchActive.value) {
    resetAndSearchLinks(searchString.value)
  } else {
    resetAndGetLinkList()
  }
}, 400)
function resetAndGetLinkList() {
  if (!selectedObject.value && selectedObject.value !== 0) return
  if (!Object.keys(selectedObject.value).length && selectedObject.value !== 0) return
  piniaStore.setLinksList([])
  pageNumber.value = 0
  getItems(store.getters.selectedObjectId, pageNumber.value)
}
function resetAndSearchLinks(newVal: string) {
  if (!selectedObject.value && selectedObject.value !== 0) return
  if (!Object.keys(selectedObject.value).length && selectedObject.value !== 0) return
  piniaStore.setLinksList([])
  pageNumber.value = 0
  getSearchItems(store.getters.selectedObjectId, newVal, 0)
}
function searchStringValue(searchValue: string) {
  if (searchValue.length >= 1) clearInput.value = false
  searchString.value = searchValue
}
function showModalCreateLink() {
  clearInput.value = true
  piniaStore.setSelectedLink(null)
  piniaStore.setCurrentOverlayModalPinia('editLink')
  piniaStore.setShowOverlayModalPinia(true)
}

watch(linksList, (newList, oldList) => {
  // remove one item from list when a link deleted
  if (!linksPage.value?.totalElements) return
  // Todo: The line below could make the totalElements value incorrect for the last page
  if (newList.length < oldList.length) linksPage.value.totalElements--
})
watch(
  linksUpdated,
  (newLinksArray, oldLinksArray) => {
    if (newLinksArray.length && newLinksArray.length > oldLinksArray.length) {
      piniaStore.setLinksList([])
      pageNumber.value = 0
      getItems(store.getters.selectedObjectId, pageNumber.value)
    }
  },
  { deep: true }
)
watch(includeChildObject, () => {
  refreshList()
})
watch(searchString, (newVal, oldVal) => {
  if (newVal !== oldVal) refreshListDebounce()
})
watch(
  selectedObject,
  async () => {
    refreshList()
    piniaStore.setSelectedLink(null)
  },
  { immediate: true }
)
onUnmounted(() => {
  piniaStore.setSelectedLink(null)
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    id: "links-dashboard",
    class: _normalizeClass({ fullscreen: isFeatureFullscreen.value && !mobileScreen.value, mobile: mobileScreen.value })
  }, [
    (showConfirmDelete.value)
      ? (_openBlock(), _createBlock(OverlayCard, {
          key: 0,
          class: _normalizeClass(["overlay-confirm-delete", {
        'overlay-confirm-delete__mobile': mobileScreen.value,
        'overlay-confirm-delete__fullscreen': isFeatureFullscreen.value && !mobileScreen.value
      }]),
          onCloseOverlay: _cache[2] || (_cache[2] = ($event: any) => (showConfirmDelete.value = false))
        }, {
          default: _withCtx(() => [
            _createVNode(ConfirmDeleteAttachmentCard, {
              buttonText: _unref(t)('general__text__delete') + ' ' + _unref(t)('general__data__link(s)').toLowerCase(),
              onCloseOverlay: _cache[0] || (_cache[0] = ($event: any) => (showConfirmDelete.value = false)),
              onConfirmDelete: _cache[1] || (_cache[1] = ($event: any) => (deleteLink()))
            }, null, 8, ["buttonText"])
          ]),
          _: 1
        }, 8, ["class"]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_1, [
      _createVNode(InputSearch, {
        class: "search-container__input",
        clearInput: clearInput.value,
        placeholderText: _unref(t)('general__text__search-placeholder-links'),
        onEmitSearchString: searchStringValue
      }, null, 8, ["clearInput", "placeholderText"])
    ]),
    _createElementVNode("div", _hoisted_2, [
      _createVNode(CheckBoxStandard, {
        class: "include-child-object-container__checkbox",
        checked: includeChildObject.value,
        disabled: selectedObject.value === 0,
        title: _unref(t)('general__text__objects-include-child'),
        onValueChanged: _cache[3] || (_cache[3] = ($event: any) => (includeChildObject.value = !includeChildObject.value))
      }, null, 8, ["checked", "disabled", "title"]),
      _createVNode(InfoLabel, {
        class: "include-child-object-container__info",
        info: _unref(t)('tooltip__links-include-child-objects')
      }, null, 8, ["info"])
    ]),
    _createElementVNode("div", _hoisted_3, [
      _createVNode(TableLinksMini, {
        fields: fields.value,
        items: linksList.value,
        onArchiveLink: archiveLink,
        onDeleteLink: _cache[4] || (_cache[4] = ($event: any) => (showConfirmDelete.value = true)),
        onLoadMoreLinks: loadMoreLinks
      }, null, 8, ["fields", "items"])
    ]),
    _createElementVNode("div", {
      class: _normalizeClass(["bottom-container", { 'bottom-container__no-button': !_unref(store).getters.hasLinkCreatorAccess }])
    }, [
      (_unref(store).getters.hasLinkCreatorAccess)
        ? (_openBlock(), _createBlock(ButtonOutline, {
            key: 0,
            buttonText: _unref(t)('general__data__link-add-new'),
            iconLeft: require('@/design-system/assets/icons/Icon-Plus-Small.svg'),
            size: 'small',
            style: {"width":"100%"},
            onOnClicked: showModalCreateLink
          }, null, 8, ["buttonText", "iconLeft"]))
        : _createCommentVNode("", true)
    ], 2)
  ], 2))
}
}

})